import * as R from 'ramda';
import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// components
import { Table } from '../../../components/table';
// forms
import { FormGroupWrapper } from '../../../forms';
// helpers/constants
import * as G from '../../../helpers';
// hooks
import useEditViewHook from '../../../hooks/use-edit-view';
// feature fleet-profile
import { FormGroupTitle } from './form-group-title';
import { toggleFormGroupTableDetails } from '../actions';
import { tableSettings } from '../settings/table-settings';
import { useFormGroupOperations } from '../hooks/use-form-group-operations';
import {
  makeSelectPaginationByGroupName,
  makeSelectTotalCountByGroupName,
  makeSelectListLoadingByGroupName,
} from '../selectors';
//////////////////////////////////////////////////

const makeReport = ({ report, itemList, hideEmptyFields }) => {
  if (R.not(hideEmptyFields)) return report;

  const fields = R.filter(
    ({ name, checkFieldFunction }) => {
      if (G.isFunction(checkFieldFunction)) return checkFieldFunction(itemList);

      return G.isNotNilAndNotEmpty(R.path([0, ...R.split('.', name)], itemList));
    },
    report.fields,
  );

  return { fields };
};

const useTableProps = (props) => {
  const {
    report,
    itemList,
    groupName,
    endpoints,
    paidByTrip,
    configGroup,
    isReadOnlyMode,
    columnSettings,
    hideEmptyFields,
    downloadOptions,
    handleRemoveItem,
    handleShowDetails,
    primaryObjectGuid,
    getEditPermissions,
    primaryObjectGuidKey,
    getRemovePermissions,
    handlePreviewDocument,
    handleDownloadDocument,
    withUiFiltersAndEditView,
    handleCreateOrUpdateItem,
    tableCallbackDataProps = [],
    additionalTableSettings = {},
    handleOpenLastKnownLocationAudit,
    handleUpdateAdvancePaymentStatus,
    includeItemListToTablePropsMemoDeps = false,
  } = props;

  const dispatch = useDispatch();

  const { uiReportFields, handleOpenEditView } = useEditViewHook(props);

  let deps = G.ifElse(
    R.or(hideEmptyFields, includeItemListToTablePropsMemoDeps),
    [itemList, groupName, configGroup],
    [groupName],
  );

  if (withUiFiltersAndEditView) deps = [...deps, uiReportFields];

  const tableProps = useMemo(() => {
    const actionButtonsMap = {
      edit: {
        iconName: 'pencil',
        getPermissions: getEditPermissions,
        action: (guid, entity) => handleCreateOrUpdateItem(entity),
      },
      updateAdvancePaymentStatus: {
        iconName: 'pencil',
        action: (guid, entity) => handleUpdateAdvancePaymentStatus(entity),
      },
      remove: {
        iconName: 'trash',
        action: handleRemoveItem,
        getPermissions: getRemovePermissions,
        enableIfEditable: R.equals(groupName, 'inspections'),
      },
      removePayrollCompensation: {
        iconName: 'trash',
        action: handleRemoveItem,
        permissions: G.ifElse(R.not(paidByTrip), ''),
      },
      lastKnownLocationAudit: {
        iconSize: [18, 18],
        iconName: 'renderAuditHistoryIcon',
        action: handleOpenLastKnownLocationAudit,
      },
      downloadDocument: {
        iconName: 'downloadDocument',
        getPermissions: ({ fileUri }) => G.ifElse(R.isNil(fileUri), ''),
        action: (guid, entity) => handleDownloadDocument(
          entity,
          downloadOptions,
          { primaryObjectGuidKey, endpointName: G.getPropFromObject('downloadDocument', endpoints) },
        ),
      },
      previewDocument: {
        iconName: 'eye',
        getPermissions: ({ fileUri }) => G.ifElse(R.isNil(fileUri), ''),
        action: (guid, entity) => handlePreviewDocument(
          entity,
          downloadOptions,
          { primaryObjectGuidKey, endpointName: G.getPropFromObject('downloadDocument', endpoints) },
        ),
      },
      showDetails: {
        iconName: 'eye2',
        action: (guid, entity) => handleShowDetails(guid, primaryObjectGuid, entity),
      },
    };

    const actionsPicker = R.compose(
      R.uniq,
      (actions) => (
        R.and(isReadOnlyMode, G.isNotEmpty(actions)) ?
        R.prepend(
          'showDetails',
          R.without(['edit', 'remove', 'removePayrollCompensation', 'updateAdvancePaymentStatus'], actions),
        ) :
        actions
      ),
      R.propOr(['edit', 'remove'], 'actionsPicker'),
    )(props);

    const actionButtons = R.values(R.pick(actionsPicker, actionButtonsMap));

    const sortedReport = G.isNilOrEmpty(uiReportFields) ? report : G.getSortedReport(uiReportFields, report);

    const readOnlyTableSettings = G.ifElse(
      isReadOnlyMode,
      {
        checkBoxCellJustifyContent: G.ifElse(R.equals(R.length(actionButtons), 1), 'center', 'flex-start'),
      },
      {},
    );

    return {
      actionButtons,
      columnSettings,
      callbackData: R.pick(tableCallbackDataProps, props),
      handleClickEditOrder: G.ifElse(withUiFiltersAndEditView, handleOpenEditView, null),
      toggle: ({ guid }) => dispatch(toggleFormGroupTableDetails({ guid, groupName })),
      tableSettings: R.mergeAll([tableSettings, additionalTableSettings, readOnlyTableSettings]),
      filterProps: withUiFiltersAndEditView ? G.getFilterPropsFromColumnSettings(columnSettings) : null,
      report: withUiFiltersAndEditView ? sortedReport : makeReport({ report, itemList, hideEmptyFields }),
    };
  }, deps);

  return tableProps;
};

const FormGroupTable = memo((props) => {
  const {
    margin,
    itemList,
    groupName,
    rowBgFunction,
    isOpened = true,
    tableInnerWidth,
    getItemListRequest,
    withResizableColumns,
    handleLoadMoreEntities,
    withUiFiltersAndEditView,
    hasFormGroupTitle = true,
  } = props;

  const loading = useSelector(makeSelectListLoadingByGroupName(groupName));
  const totalCount = useSelector(makeSelectTotalCountByGroupName(groupName));
  const pagination = useSelector(makeSelectPaginationByGroupName(groupName));

  const tableProps = useTableProps(props);

  return (
    <FormGroupWrapper m={margin} isOpened={isOpened}>
      {hasFormGroupTitle && <FormGroupTitle {...props} />}
      {
        G.isTrue(isOpened) &&
        <Table
          {...tableProps}
          loading={loading}
          itemList={itemList}
          pagination={pagination}
          totalCount={totalCount}
          rowBgFunction={rowBgFunction}
          tableInnerWidth={tableInnerWidth}
          showEditView={withUiFiltersAndEditView}
          withResizableColumns={withResizableColumns}
          shouldUseUIFilters={withUiFiltersAndEditView}
          useSearchableColumns={withUiFiltersAndEditView}
          useNewTitleFilterInputs={withUiFiltersAndEditView}
          handleLoadMoreEntities={R.or(handleLoadMoreEntities, getItemListRequest)}
        />
      }
    </FormGroupWrapper>
  );
});

export const FormGroupTableWithSimpleCrudOperations = memo((props) => {
  const formGroupOperations = useFormGroupOperations(props);

  return <FormGroupTable {...props} {...formGroupOperations} />;
});

export default FormGroupTable;
